import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import toast from 'react-hot-toast';

const Login: React.FC = () => {
  const { login, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  // Load remembered credentials from localStorage
  const rememberedEmployeeId = localStorage.getItem('rememberedEmployeeId') || '';
  const rememberedPassword = localStorage.getItem('rememberedPassword') || '';
  const remembered = localStorage.getItem('rememberMe') === 'true';

  const [employeeId, setEmployeeId] = useState(rememberedEmployeeId);
  const [password, setPassword] = useState(rememberedPassword);
  const [rememberMe, setRememberMe] = useState(remembered);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmployeeId, setForgotEmployeeId] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  useEffect(() => {
    const err = searchParams.get('ssoError');
    if (!err) return;
    setSsoError(
      err === 'missing_code'
        ? 'SSO sign-in was missing a code. Please sign in with your password.'
        : 'SSO sign-in failed or the code expired. Please sign in with your password.',
    );
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rememberMe) {
      localStorage.setItem('rememberedEmployeeId', employeeId);
      localStorage.setItem('rememberedPassword', password);
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberedEmployeeId');
      localStorage.removeItem('rememberedPassword');
      localStorage.setItem('rememberMe', 'false');
    }
    await login({ employeeId, password });
    // Navigation is handled in AuthContext
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmployeeId.trim()) {
      toast.error('Enter your DRB ID');
      return;
    }
    try {
      setForgotLoading(true);
      const result = await authService.forgotPassword(forgotEmployeeId.trim());
      toast.success(result.message);
      setShowForgotPassword(false);
      setForgotEmployeeId('');
    } catch (error: any) {
      // apiClient already displays the error toast
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - HRMS Enterprise</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 sm:space-y-8">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
              Sign in to HRMS
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Enter your credentials to access your account
            </p>
          </div>
          
          <div className="bg-white py-6 sm:py-8 px-4 sm:px-6 shadow-lg rounded-lg">
            {ssoError && (
              <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {ssoError}
              </div>
            )}
            {!showForgotPassword ? (
            <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700 mb-1">
                  DRB ID
                </label>
                <input
                  id="employeeId"
                  name="employeeId"
                  type="text"
                  autoComplete="username"
                  required
                  className="input w-full"
                  placeholder="Enter your DRB ID"
                  value={employeeId}
                  onChange={e => setEmployeeId(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Administrator signs in with Login ID 0001
                </p>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="input w-full"
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </div>
              
              <div className="pt-2 flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    name="rememberMe"
                    type="checkbox"
                    className="mr-2"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                  />
                  <label htmlFor="rememberMe" className="text-sm text-gray-700 select-none">
                    Remember me
                  </label>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary w-32 py-3 text-sm sm:text-base"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
              <div className="text-right">
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    setForgotEmployeeId(employeeId);
                    setShowForgotPassword(true);
                  }}
                >
                  Forgot password?
                </button>
              </div>
            </form>
            ) : (
            <form className="space-y-4" onSubmit={handleForgotPassword}>
              <h2 className="text-lg font-semibold text-gray-900">Forgot password</h2>
              <p className="text-sm text-gray-600">
                Enter your DRB ID. A new password will be sent by SMS to the mobile number on file.
              </p>
              <div>
                <label htmlFor="forgotEmployeeId" className="block text-sm font-medium text-gray-700 mb-1">
                  DRB ID
                </label>
                <input
                  id="forgotEmployeeId"
                  type="text"
                  required
                  className="input w-full"
                  placeholder="Enter your DRB ID"
                  value={forgotEmployeeId}
                  onChange={e => setForgotEmployeeId(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Back to sign in
                </button>
                <button
                  type="submit"
                  className="btn btn-primary py-2 px-4 text-sm"
                  disabled={forgotLoading}
                >
                  {forgotLoading ? 'Sending...' : 'Send new password'}
                </button>
              </div>
            </form>
            )}
          </div>
          
          {/* Demo credentials hint - Only show in development environment */}
          {import.meta.env.DEV && (
            <div className="text-center">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-blue-800 font-medium mb-1">Demo Credentials (Dev Only)</p>
                <p className="text-xs text-blue-700">
                  Admin Login ID: <span className="font-mono">0001</span>
                </p>
                <p className="text-xs text-blue-700">
                  Password: <span className="font-mono">admin123</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Login;
